import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { api } from '../../services/api';

// Fetch once per session and cache in module scope
let banksCache: string[] | null = null;
let fetchPromise: Promise<string[]> | null = null;

function loadBanks(): Promise<string[]> {
    if (banksCache) return Promise.resolve(banksCache);
    if (fetchPromise) return fetchPromise;
    fetchPromise = api.get(API_ENDPOINTS.getBanks())
        .then(res => {
            const data = res.data;
            let list: string[] = [];
            if (Array.isArray(data)) {
                list = data.map((b: any) => typeof b === 'string' ? b : b.name || b.bank_name || String(b));
            } else if (data?.banks) {
                list = (Array.isArray(data.banks) ? data.banks : [])
                    .map((b: any) => typeof b === 'string' ? b : b.name || b.bank_name || String(b));
            }
            banksCache = list;
            return list;
        })
        .catch(() => { fetchPromise = null; return []; });
    return fetchPromise;
}

interface BankNameInputProps {
    value: string;
    onChange: (value: string) => void;
    className?: string;
    placeholder?: string;
    disabled?: boolean;
}

const OTHER = '__OTHER__';

const BankNameInput: React.FC<BankNameInputProps> = ({
    value,
    onChange,
    className = '',
    placeholder = 'Search bank name',
    disabled = false,
}) => {
    const [banks, setBanks] = useState<string[]>(banksCache || []);
    const [open, setOpen] = useState(false);
    const [isOther, setIsOther] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const otherInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => { loadBanks().then(setBanks); }, []);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Focus the custom input when "Other" is selected
    useEffect(() => {
        if (isOther) otherInputRef.current?.focus();
    }, [isOther]);

    const filtered = value.trim()
        ? banks.filter(b => b.toLowerCase().includes(value.toLowerCase()))
        : banks;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value.replace(/[^a-zA-Z ]/g, ''));
        setOpen(true);
    };

    const handleSelect = (bank: string) => {
        if (bank === OTHER) {
            setIsOther(true);
            onChange('');
            setOpen(false);
        } else {
            onChange(bank);
            setOpen(false);
        }
    };

    const handleBackToSearch = () => {
        setIsOther(false);
        onChange('');
    };

    // "Other" free-text mode
    if (isOther) {
        return (
            <div ref={containerRef} className="relative">
                <input
                    ref={otherInputRef}
                    type="text"
                    value={value}
                    onChange={e => onChange(e.target.value.replace(/[^a-zA-Z ]/g, ''))}
                    placeholder="Enter bank name"
                    className={className}
                    autoComplete="off"
                    readOnly={disabled}
                />
                {!disabled && (
                    <button
                        type="button"
                        onMouseDown={handleBackToSearch}
                        title="Back to search"
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                    >
                        <X size={13} />
                    </button>
                )}
            </div>
        );
    }

    // Normal searchable dropdown mode
    return (
        <div ref={containerRef} className="relative">
            <div className="relative">
                <input
                    type="text"
                    value={value}
                    onChange={handleChange}
                    onFocus={() => !disabled && setOpen(true)}
                    onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
                    placeholder={placeholder}
                    className={className}
                    autoComplete="off"
                    readOnly={disabled}
                />
                <ChevronDown
                    size={14}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
                />
            </div>

            {open && !disabled && (
                <ul className="absolute z-50 top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg py-1">
                    {filtered.map(bank => (
                        <li
                            key={bank}
                            onMouseDown={() => handleSelect(bank)}
                            className={`px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-[#1B4332]/10 hover:text-[#1B4332] ${bank === value ? 'bg-[#1B4332]/10 text-[#1B4332] font-semibold' : 'text-slate-700'}`}
                        >
                            {bank}
                        </li>
                    ))}
                    {/* Always show Other at the bottom */}
                    <li
                        onMouseDown={() => handleSelect(OTHER)}
                        className="px-3 py-2 text-sm cursor-pointer transition-colors border-t border-slate-100 mt-1 text-slate-500 hover:bg-slate-50 hover:text-slate-700 italic"
                    >
                        Other (enter manually)
                    </li>
                </ul>
            )}
        </div>
    );
};

export default BankNameInput;
